import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchLayerSections,
  fetchModelSections,
  fetchModelsFingerprint,
} from '../services/modelsApi';
import { extractDatabaseHolePresets, stripDatabaseHoles } from '../Utils/holeQuery';
import {
  readCatalogueSnapshot,
  saveCatalogueSnapshot,
  sectionsFromModelCache,
} from '../Utils/catalogueSnapshot';

const NON_FILE_TYPES = new Set([
  'databaseHoles',
  'holes',
  'blockmodel',
  'cachedholes',
  'holesassays',
  'assays',
]);

const POLL_VISIBLE_MS = 20 * 1000;
const POLL_HIDDEN_MS = 2 * 60 * 1000;

async function fetchHolePresetSections() {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const url = `${base}/hole-presets.json`;
  const tryParse = async (res) => {
    if (!res?.ok) return [];
    const json = await res.json();
    if (Array.isArray(json)) return json;
    if (Array.isArray(json?.sections)) return json.sections;
    return [];
  };
  try {
    const res = await fetch(`${url}?t=${Date.now()}`, { cache: 'no-store' });
    const parsed = await tryParse(res);
    if (parsed.length) return parsed;
  } catch (e) {
    console.warn('[useModelCatalogue] hole-presets.json network failed', e?.message || e);
  }
  try {
    const res = await fetch(url);
    return await tryParse(res);
  } catch (e) {
    console.warn('[useModelCatalogue] hole-presets.json failed', e?.message || e);
    return [];
  }
}

/** Split hole-presets.json into DB-style layers only (holes). GLBs and zipped block models come from list_models.php. */
export function splitLegacyModelList(list = []) {
  const dbSections = [];

  (list || []).forEach((section) => {
    const dbModels = (section.models || []).filter((m) => NON_FILE_TYPES.has(m.type || 'glb'));
    if (dbModels.length) {
      dbSections.push({ group: section.group, name: section.name, models: dbModels });
    }
  });

  return { dbSections };
}

function zipBasename(urlOrPath) {
  return String(urlOrPath || '')
    .split('?')[0]
    .split('/')
    .pop()
    .toLowerCase();
}

function catalogueKeySet(m) {
  const keys = new Set();
  const add = (v) => {
    const s = String(v || '').trim().toLowerCase();
    if (!s) return;
    keys.add(s);
    keys.add(s.replace(/\.(zip|glb)$/i, ''));
  };
  add(zipBasename(m.url));
  add(zipBasename(m.path));
  add(zipBasename(m.name));
  add(m.displayName);
  add(String(m.name || '').split('/').pop());
  return keys;
}

function dropDuplicateBlockModels(dbSections, fileSections) {
  const diskKeys = new Set();
  (fileSections || []).forEach((section) => {
    (section.models || []).forEach((m) => {
      if (String(m.type || '').toLowerCase() !== 'blockmodel') return;
      catalogueKeySet(m).forEach((k) => diskKeys.add(k));
    });
  });
  if (!diskKeys.size) return dbSections;

  return (dbSections || [])
    .map((section) => ({
      ...section,
      models: (section.models || []).filter((m) => {
        if (String(m.type || '').toLowerCase() !== 'blockmodel') return true;
        for (const k of catalogueKeySet(m)) {
          if (diskKeys.has(k)) return false;
        }
        return true;
      }),
    }))
    .filter((section) => (section.models || []).length > 0);
}

function mergeSections(...lists) {
  const map = new Map();
  lists.flat().forEach((section) => {
    if (!section) return;
    const key = `${section.group}\0${section.name}`;
    if (!map.has(key)) {
      map.set(key, { group: section.group, name: section.name, models: [...(section.models || [])] });
    } else {
      map.get(key).models.push(...(section.models || []));
    }
  });
  return Array.from(map.values());
}

/**
 * Load filesystem GLB + zip sections from list_models.php + DB layers from list_layers.php.
 * Polls disk fingerprint so renames done directly on the server are picked up.
 */
export function useModelCatalogue() {
  const [modelList, setModelList] = useState([]);
  const [holePresets, setHolePresets] = useState([]);
  const [tree, setTree] = useState(null);
  const [flat, setFlat] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [source, setSource] = useState('loading');
  const [fingerprint, setFingerprint] = useState(null);
  const fingerprintRef = useRef(null);
  const refreshingRef = useRef(false);
  const queuedRefreshRef = useRef(false);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (refreshingRef.current) {
      queuedRefreshRef.current = true;
      return;
    }
    refreshingRef.current = true;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const legacy = splitLegacyModelList(await fetchHolePresetSections());
      const snapshot = readCatalogueSnapshot();

      let glbSections = [];
      let dbSections = legacy.dbSections;
      let nextTree = null;
      let nextFlat = [];
      let usedApi = false;
      let usedOffline = false;
      const errors = [];
      let modelsPayload = null;
      let layersPayload = null;

      try {
        const modelsData = await fetchModelSections();
        modelsPayload = modelsData;
        if (modelsData.sections?.length) {
          glbSections = modelsData.sections;
          usedApi = true;
        } else {
          console.warn('[useModelCatalogue] list_models returned 0 sections', modelsData.meta || modelsData);
          errors.push(
            `list_models returned 0 GLBs (root: ${modelsData.modelsRoot || modelsData.meta?.modelsRoot || 'unknown'})`
          );
        }
        nextTree = modelsData.tree || null;
        nextFlat = modelsData.flat || [];
        const fp = modelsData.fingerprint || modelsData.meta?.fingerprint || null;
        fingerprintRef.current = fp;
        setFingerprint(fp);
      } catch (e) {
        console.warn('[useModelCatalogue] list_models failed', e);
        errors.push(e.message || 'list_models failed');
        if (snapshot?.models?.sections?.length) {
          glbSections = snapshot.models.sections;
          nextTree = snapshot.models.tree || null;
          nextFlat = snapshot.models.flat || [];
          usedOffline = true;
        } else {
          const cachedSections = await sectionsFromModelCache();
          if (cachedSections.length) {
            glbSections = cachedSections;
            usedOffline = true;
          }
        }
      }

      try {
        const layersData = await fetchLayerSections();
        layersPayload = layersData;
        if (layersData.sections?.length) {
          dbSections = dropDuplicateBlockModels(layersData.sections, glbSections);
          usedApi = true;
        }
      } catch (e) {
        console.warn('[useModelCatalogue] list_layers failed, using cached layers / hole-presets.json', e);
        if (snapshot?.layers?.sections?.length) {
          dbSections = dropDuplicateBlockModels(snapshot.layers.sections, glbSections);
          usedOffline = true;
        }
      }

      if (usedApi && (modelsPayload || layersPayload)) {
        saveCatalogueSnapshot({
          models: modelsPayload || snapshot?.models || null,
          layers: layersPayload || snapshot?.layers || null,
        });
      }

      const merged = mergeSections(glbSections, dropDuplicateBlockModels(dbSections, glbSections));
      setHolePresets(extractDatabaseHolePresets(merged));
      setModelList(stripDatabaseHoles(merged));
      setTree(nextTree);
      setFlat(nextFlat);
      setSource(usedApi ? 'api' : usedOffline ? 'offline' : 'legacy');
      setError(usedOffline ? null : errors.length ? errors.join('; ') : null);
      setLoading(false);
    } finally {
      refreshingRef.current = false;
      if (queuedRefreshRef.current) {
        queuedRefreshRef.current = false;
        refresh({ silent: true });
      }
    }
  }, []);

  useEffect(() => {
    refresh();
    const onChanged = () => refresh({ silent: true });
    const onOnline = () => refresh({ silent: true });
    window.addEventListener('models-changed', onChanged);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('models-changed', onChanged);
      window.removeEventListener('online', onOnline);
    };
  }, [refresh]);

  // Detect filesystem replaces/renames/adds (no File Management event)
  useEffect(() => {
    let cancelled = false;
    let id;
    const tick = async () => {
      if (cancelled || (typeof navigator !== 'undefined' && navigator.onLine === false)) return;
      try {
        const data = await fetchModelsFingerprint();
        const next = data.fingerprint || data.meta?.fingerprint || null;
        if (next && fingerprintRef.current && next !== fingerprintRef.current) {
          console.log('[useModelCatalogue] models folder changed on disk — refreshing catalogue');
          window.dispatchEvent(
            new CustomEvent('models-changed', { detail: { reason: 'disk-fingerprint', fingerprint: next } })
          );
        } else if (next && !fingerprintRef.current) {
          fingerprintRef.current = next;
          setFingerprint(next);
        }
      } catch {
        // ignore transient poll errors
      }
    };
    const schedule = () => {
      window.clearInterval(id);
      id = window.setInterval(tick, document.hidden ? POLL_HIDDEN_MS : POLL_VISIBLE_MS);
    };
    schedule();
    const onVisible = () => {
      schedule();
      if (!document.hidden) tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', tick);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', tick);
    };
  }, []);

  return { modelList, holePresets, tree, flat, loading, error, source, fingerprint, refresh };
}
